import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { PipelineData, PipelineStep, PipelineSource, LogEntry, Artifact } from '../../services/mock-pipeline-data'
import { newHttpService } from '../../services/new-http-service'
import { createJobRunMonitor, getUiRunState, JobRunStatus, JobRunUpdate, jobRunKey, isRealGlueRun, TERMINAL_STATES } from '../../services/job-run-monitor'
import { utilService } from '../../services/util-service'
import { useCachedFetch } from '../../hooks/use-cached-fetch'
import { getSystemScheduledJobs, systemHasSchedule } from '../../services/schedule-utils'
import { envService } from '../../services/env-service'
import { updateAuditControlMutation } from '../util-cmps/graphql-mutations/update-audit-control'
import { buildPairKey } from '../alert-events/alert-event-feed'
import HistoricalWorkingPaperModal from './historical-working-paper-modal'
import { AwsEtlLinks } from './aws-etl-links'
import { SessionFindings } from './session-findings'

export type { JobRunStatus } from '../../services/job-run-monitor'

interface ActivityLogEntry {
  time: string
  type: 'trigger' | 'status' | 'complete' | 'error' | 'lambda'
  message: string
  source?: string // groups entries by job/lambda name
  job_run_id?: string
  aws_region?: string
}

interface RunGroup {
  source: string
  entries: ActivityLogEntry[]
  runJobId?: string
  runRegion?: string
  runIdx: number
}

interface Iteration {
  start: string
  label: string
  handlerTimestamp?: string
  forcedStages: Set<string>
  records: Record<string, any[]>
  controlResultId?: number
  isVirtual?: boolean
  virtualAlert?: any
}

// Reactive store: persists job runs + activity log across mount/unmount AND page reloads (localStorage)
const LS_RUNS_KEY = 'auditech_job_runs'
const LS_LOG_KEY = 'auditech_activity_log'
const LS_LAMBDA_LOGS_KEY = 'auditech_lambda_logs'
const LS_MAX_AGE_MS = 24 * 60 * 60 * 1000 // 1 day

function loadFromLS<T>(key: string): Map<string, T> {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return new Map()
    const parsed = JSON.parse(raw) as { ts: number; data: [string, T][] }
    if (Date.now() - parsed.ts > LS_MAX_AGE_MS) { localStorage.removeItem(key); return new Map() }
    return new Map(parsed.data)
  } catch { return new Map() }
}

function saveToLS<T>(key: string, map: Map<string, T>) {
  try {
    const entries = Array.from(map.entries())
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data: entries }))
  } catch { /* localStorage full or unavailable */ }
}

const jobRunStore = loadFromLS<JobRunStatus[]>(LS_RUNS_KEY)
const activityLogStore = loadFromLS<ActivityLogEntry[]>(LS_LOG_KEY)
// Keyed by lambdaRunId (globally unique) → persisted logs for that invocation
const lambdaLogStore = loadFromLS<{ logs: any[]; logGroup?: string }>(LS_LAMBDA_LOGS_KEY)
const jobRunListeners = new Set<() => void>()

function notifyJobRunListeners() {
  jobRunListeners.forEach(fn => fn())
}

function setStoreRuns(acId: string, runs: JobRunStatus[]) {
  if (runs.length > 0) {
    jobRunStore.set(acId, runs)
  } else {
    jobRunStore.delete(acId)
  }
  saveToLS(LS_RUNS_KEY, jobRunStore)
  notifyJobRunListeners()
}

function addLogEntry(acId: string, entry: ActivityLogEntry) {
  const log = activityLogStore.get(acId) || []
  log.push(entry)
  activityLogStore.set(acId, log)
  saveToLS(LS_LOG_KEY, activityLogStore)
  notifyJobRunListeners()
}

function nowTime(): string {
  const now = new Date()
  const date = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return `${date} ${time}`
}

function pruneLogStoreIfNewDay() {
  try {
    const raw = localStorage.getItem(LS_LOG_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as { ts: number; data: any[] }
    const saved = new Date(parsed.ts)
    const now = new Date()
    const sameDay =
      saved.getFullYear() === now.getFullYear() &&
      saved.getMonth() === now.getMonth() &&
      saved.getDate() === now.getDate()
    if (!sameDay) {
      activityLogStore.clear()
      saveToLS(LS_LOG_KEY, activityLogStore)
    }
  } catch { /* ignore */ }
}
pruneLogStoreIfNewDay()

// One monitor serves the table and every open pipeline, including restored runs.
const jobRunMonitor = createJobRunMonitor({
  getRuns: () => Array.from(jobRunStore.values()).flat(),
  subscribe: listener => {
    jobRunListeners.add(listener)
    return () => { jobRunListeners.delete(listener) }
  },
  applyStatuses: (statuses: JobRunUpdate[]) => {
    const updates = new Map(statuses.filter(status => status.state !== 'UNKNOWN').map(status => [jobRunKey(status), status]))
    let changed = false
    jobRunStore.forEach((runs, acId) => {
      let controlChanged = false
      const updated = runs.map(run => {
        const match = updates.get(jobRunKey(run))
        if (!match || match.state === run.state || TERMINAL_STATES.includes(run.state)) return run
        controlChanged = true
        const isTerminal = TERMINAL_STATES.includes(match.state)
        const type: ActivityLogEntry['type'] = ['FAILED', 'ERROR', 'TIMEOUT'].includes(match.state) ? 'error' : isTerminal ? 'complete' : 'status'
        const suffix = match.execution_time ? ` (${Math.round(match.execution_time)}s)` : ''
        const errSuffix = match.error_message && isTerminal ? ` — ${match.error_message}` : ''
        const log = activityLogStore.get(acId) || []
        activityLogStore.set(acId, [...log, {
          time: nowTime(), type, message: `${match.state}${suffix}${errSuffix}`,
          source: run.system_name, job_run_id: run.job_run_id, aws_region: run.aws_region,
        }])
        return { ...run, ...match }
      })
      if (controlChanged) {
        jobRunStore.set(acId, updated)
        changed = true
      }
    })
    if (changed) {
      saveToLS(LS_RUNS_KEY, jobRunStore)
      saveToLS(LS_LOG_KEY, activityLogStore)
      notifyJobRunListeners()
    }
  },
})

/** Returns a summary for a given AC: 'running' | 'done' | null */
export function useJobRunIndicator(auditControlId: string | number): 'running' | 'done' | null {
  const acId = String(auditControlId)
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1)
    jobRunListeners.add(listener)
    return () => { jobRunListeners.delete(listener) }
  }, [])

  const runs = jobRunStore.get(acId)
  if (!runs || runs.length === 0) return null
  return getUiRunState(runs) === 'running' ? 'running' : 'done'
}

/** Returns all AC IDs that have job runs, with their aggregate status */
export function useJobRunStates(): Map<string, 'running' | 'success' | 'error'> {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1)
    jobRunListeners.add(listener)
    const stopMonitoring = jobRunMonitor.start()
    return () => { jobRunListeners.delete(listener); stopMonitoring() }
  }, [])

  const result = new Map<string, 'running' | 'success' | 'error'>()
  jobRunStore.forEach((runs, acId) => {
    const state = getUiRunState(runs)
    if (state) result.set(acId, state)
  })
  return result
}

interface ExecutionPipelineProps {
  auditControlId: string
  auditControl?: any
  companyName?: string
  siblingControls?: Array<{
    id: string
    systems: string[]
  }>
  onDataChange?: () => void
}

const buildCloudWatchUrl = (jobRunId: string, region: string, logType: 'output' | 'error'): string => {
  const logGroup = encodeURIComponent(encodeURIComponent(`/aws-glue/python-jobs/${logType}`))
  return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${logGroup}/log-events/${jobRunId}`
}

const STEP_STAGES = ['consolidated', 'rule-manager', 'handler'] as const

const STAGE_LABELS: Record<string, string> = {
  'consolidated': 'Consolidation',
  'rule-manager': 'Rule Manager',
  'handler': 'Handler',
}

const ALERT_TYPE_LABELS: Record<string, string> = {
  etl_failure:    'ETL Failure',
  lambda_failure: 'Lambda Failure',
  ecs_failure:    'ECS Failure',
  old_agent_data: 'Old Agent Data',
  old_email_data: 'Old Email Data',
  old_sftp_data:  'Old SFTP Data',
}

const hasError = (rec: any): boolean =>
  !!rec.metadata_json?.error

const formatTimestamp = (ts: string): string => utilService.formatDateTime(ts)

export const isSameCalendarDay = (left: string, right: string): boolean => {
  const a = new Date(left)
  const b = new Date(right)
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
}

export const shouldShowWorkingPaperWarning = (args: {
  hasAnyHandlerHistory: boolean
  workingPaperSourceIterationExists: boolean
  isWorkingPaperAligned: boolean
  currentSessionHasControlResultId: boolean
  isWorkingPaperSameDayAsCurrentSession: boolean
}): boolean =>
  args.hasAnyHandlerHistory
  && args.workingPaperSourceIterationExists
  && !args.isWorkingPaperAligned
  && (args.currentSessionHasControlResultId || !args.isWorkingPaperSameDayAsCurrentSession)

const getLatestTimestamp = (records: any[]): string | undefined => {
  if (records.length === 0) return undefined
  const latest = records.reduce((a: any, b: any) =>
    (a.max_created_at || a.created_at) > (b.max_created_at || b.created_at) ? a : b
  )
  return latest.max_created_at || latest.created_at
}

const CONSOLIDATION_FRESHNESS_WINDOW_MS = 12 * 60 * 60 * 1000

// Some ETLs (Salesforce, Dynamics NAV, Oracle ERP) append a data-type suffix to the system
// name in the output key: e.g. "salesforce_users", "nav_admins". These are the only suffixes
// used across all ETL repos — derived from a survey of the actual ETL code.
const KNOWN_ETL_SUFFIXES = ['users', 'admins', 'policies', 'audit_trail', 'role_assignments', 'role_assigned', 'roles', 'employees', 'administration_activities']
const KNOWN_SYSTEM_KEY_ALIASES: Record<string, string[]> = {
  azureactivedirectory: ['azuread'],
}
const normalizeKey = (value: string): string => value.toLowerCase().replace(/[\s_-]+/g, '')
const getComparableKeys = (value: string): string[] => {
  const normalized = normalizeKey(value)
  return [normalized, ...(KNOWN_SYSTEM_KEY_ALIASES[normalized] || [])]
}
export const systemsMatch = (left: string, right: string): boolean => {
  const leftKeys = new Set(getComparableKeys(left))
  return getComparableKeys(right).some(key => leftKeys.has(key))
}
const toTimestamp = (value: string): number => {
  const ts = new Date(value).getTime()
  return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts
}
const compareTimestampsDesc = (left: string, right: string): number => toTimestamp(right) - toTimestamp(left)
const compareTimestampsAsc = (left: string, right: string): number => toTimestamp(left) - toTimestamp(right)

const findRawRecordsForSystem = (rawRecords: any[], system: any): any[] => {
  if (!system || rawRecords.length === 0) return []

  // Build candidate strings to match against output_key_name.
  // Use exact equality (or exact equality with a known ETL suffix) — plain substring matching
  // causes "azure_devops" to incorrectly match the "azure_devops_tickets" system.
  const candidates: string[] = []
  if (system.abbreviation) candidates.push(system.abbreviation)
  if (system.name) {
    candidates.push(system.name)
  }
  const allCandidates = new Set(
    candidates.flatMap(candidate => {
      const normalized = normalizeKey(candidate)
      return [normalized, ...(KNOWN_SYSTEM_KEY_ALIASES[normalized] || [])]
    })
  )

  return rawRecords.filter(r => {
    const key = normalizeKey(r.output_key_name || '')
    return Array.from(allCandidates).some(c => {
      if (key === c) return true
      if (c === 'salesforce' && key.startsWith('salesforce')) return true
      return KNOWN_ETL_SUFFIXES.some(suffix => key === `${c}${suffix}`)
    })
  })
}

const getLatestExecutionTimestamp = (records: any[]): number | null => {
  if (records.length === 0) return null
  const latest = records.reduce((current: number | null, rec: any) => {
    const ts = new Date(getTs(rec)).getTime()
    return Number.isNaN(ts) ? current : Math.max(current ?? 0, ts)
  }, null)
  return latest && latest > 0 ? latest : null
}

const getManualConsolidationWarningSystems = (
  auditControl: any,
  executions: any[],
  now: number,
  triggeredSystemNames: string[] = [],
): string[] => {
  const triggeredSystems = new Set(triggeredSystemNames.map(name => name.trim().toLowerCase()).filter(Boolean))
  const controlSystems = [
    auditControl?.control?.mainSystem,
    auditControl?.control?.hrSystem,
    auditControl?.control?.ticketSystem,
  ].filter((slot: any) => slot?.system)

  const normalizedExecutions = executions.filter((rec: any) => rec.stage === 'normalized')

  return controlSystems
    .filter((slot: any) => !!slot.isRequiredDaily)
    .filter((slot: any) => {
      const systemName = slot.system.name?.toLowerCase()
      const systemAbbreviation = slot.system.abbreviation?.toLowerCase()
      return !triggeredSystems.has(systemName) && !triggeredSystems.has(systemAbbreviation)
    })
    .filter((slot: any) => {
      const matched = findRawRecordsForSystem(normalizedExecutions, slot.system)
      const latestTs = getLatestExecutionTimestamp(matched)
      return !latestTs || (now - latestTs) > CONSOLIDATION_FRESHNESS_WINDOW_MS
    })
    .map((slot: any) => slot.system.name)
}

const getSourceStatus = (matchedRecords: any[]): 'completed' | 'exception' | 'pending' => {
  if (matchedRecords.length === 0) return 'pending'
  if (matchedRecords.some(hasError)) return 'exception'
  return 'completed'
}

const buildSources = (auditControl: any, rawRecords: any[], fallbackRaws: any[] = []): PipelineSource[] => {
  const sources: PipelineSource[] = []

  const getLatestFileName = (records: any[]): string | undefined => {
    if (records.length === 0) return undefined
    const latest = records.reduce((a: any, b: any) =>
      (a.max_created_at || a.created_at) > (b.max_created_at || b.created_at) ? a : b
    )
    return latest.file_name
  }

  // For orphan iterations (only one ETL re-run), the iteration's rawRecords may not cover
  // every system in the control. For systems missing from the iteration, fall back to the
  // latest raw in the fallback pool (caller filters it to the last 24h of the iteration).
  const buildSource = (sys: any, type: PipelineSource['type'], icon: PipelineSource['icon'], systemName: string): PipelineSource => {
    const matched = findRawRecordsForSystem(rawRecords, sys)
    if (matched.length > 0) {
      return { label: sys.name, type, icon, systemName, abbreviation: sys.abbreviation, timestamp: getLatestTimestamp(matched), status: getSourceStatus(matched), fileName: getLatestFileName(matched) }
    }
    const fallbackMatched = findRawRecordsForSystem(fallbackRaws, sys)
    if (fallbackMatched.length > 0) {
      const latest = fallbackMatched.reduce((a: any, b: any) => compareTimestampsDesc(getTs(a), getTs(b)) <= 0 ? a : b)
      return { label: sys.name, type, icon, systemName, abbreviation: sys.abbreviation, timestamp: getTs(latest), status: getSourceStatus([latest]), fileName: latest.file_name }
    }
    return { label: sys.name, type, icon, systemName, abbreviation: sys.abbreviation, status: 'pending' }
  }

  const mainSys = auditControl?.control?.mainSystem?.system
  if (mainSys?.name) sources.push(buildSource(mainSys, 'input', 'filter', 'In Scope'))
  const hrSys = auditControl?.control?.hrSystem?.system
  if (hrSys?.name) sources.push(buildSource(hrSys, 'source', 'people', 'HR'))
  const ticketSys = auditControl?.control?.ticketSystem?.system
  if (ticketSys?.name) sources.push(buildSource(ticketSys, 'source', 'ticket', 'Ticket'))
  if (sources.length === 0) {
    sources.push({ label: 'No systems', type: 'input', icon: 'filter', status: 'pending' })
  }
  return sources
}

const getTs = (rec: any): string => rec.max_created_at || rec.created_at || ''

const getIterationControlResultId = (records: Record<string, any[]>): number | undefined => {
  for (const stage of ['handler', 'rule-manager', 'consolidated', 'normalized', 'raw']) {
    for (const rec of records[stage] || []) {
      const raw = rec?.control_result_id ?? rec?.metadata_json?.control_result_id
      const id = typeof raw === 'string' ? Number(raw) : raw
      if (Number.isInteger(id) && id > 0) return id
    }
  }
  return undefined
}

const getIterationHandlerTimestamp = (records: Record<string, any[]>): string | undefined => {
  const handlers = records.handler || []
  if (handlers.length === 0) return undefined
  return handlers.reduce((a: any, b: any) => compareTimestampsDesc(getTs(a), getTs(b)) <= 0 ? a : b)
    ? getTs(handlers.reduce((a: any, b: any) => compareTimestampsDesc(getTs(a), getTs(b)) <= 0 ? a : b))
    : undefined
}

const VIRTUAL_ITERATION_ALERT_TYPES = new Set(['etl_failure'])

/**
 * Extract the timestamp suffix from a file_name.
 * e.g. "raw/vuvuzela_db_7062_1771242173.json" → "1771242173"
 *      "consolidated/vuvuzela_db__jira_7062_1771242173.json" → "1771242173"
 *      "flow-outputs/vuvuzela_db__jira_7062_1771242173_results.json" → "1771242173"
 */
const extractFileSuffix = (fileName: string): string | null => {
  if (!fileName) return null
  const base = fileName.replace(/^.*\//, '').replace(/\.json$/, '')
  const clean = base.replace(/_results$/, '')
  const match = clean.match(/_(\d{8,})$/)
  return match ? match[1] : null
}

/**
 * Extract system names from a consolidated/flow-outputs filename.
 * e.g. "consolidated/vuvuzela_db__jira_7062_1771242173.json" → ["vuvuzela_db", "jira"]
 */
const extractSystemNames = (fileName: string): string[] => {
  if (!fileName) return []
  const base = fileName.replace(/^.*\//, '').replace(/\.json$/, '').replace(/_results$/, '')
  // Remove the trailing _acId_timestamp (e.g. _7062_1771242173)
  const withoutSuffix = base.replace(/_\d+_\d{8,}$/, '')
  return withoutSuffix.split('__')
}

/**
 * Group execution records into iterations.
 *
 * Each iteration represents one "run" through (part of) the pipeline.
 * A normal run: raws → consolidation → rule-manager → handler.
 * Forced runs reuse an upstream stage's output:
 *   - Forced consolidation: reuses raw files from an older consolidation
 *   - Forced rule-manager: reruns RM on the same consolidated file
 *   - Forced handler: reruns handler on the same RM output
 *
 * The logic:
 * 1. For each consolidated suffix, sort RM records by time (ASC).
 *    Each RM record anchors a separate iteration.
 * 2. Handler records are assigned to the RM run they follow (by timestamp).
 *    Extra handlers after the last handler in a run = forced handler iterations.
 * 3. Consolidated records with no RM = incomplete iteration (stopped at consolidation).
 * 4. Orphan raw records = standalone incomplete iterations.
 *
 * Sorted newest first.
 */
export const buildIterations = (executions: any[]): Iteration[] => {
  const byStage: Record<string, any[]> = {}
  for (const rec of executions) {
    if (!byStage[rec.stage]) byStage[rec.stage] = []
    byStage[rec.stage].push(rec)
  }

  const allRaw = (byStage['raw'] || []).slice().sort((a: any, b: any) => compareTimestampsDesc(getTs(a), getTs(b)))
  const consolidated = byStage['consolidated'] || []
  const ruleManager = byStage['rule-manager'] || []
  const handler = byStage['handler'] || []

  // Index downstream by suffix, sorted by timestamp ASC (oldest first)
  const rmBySuffix: Record<string, any[]> = {}
  for (const rec of ruleManager) {
    const s = extractFileSuffix(rec.file_name)
    if (s) { if (!rmBySuffix[s]) rmBySuffix[s] = []; rmBySuffix[s].push(rec) }
  }
  for (const s of Object.keys(rmBySuffix)) {
    rmBySuffix[s].sort((a: any, b: any) => compareTimestampsAsc(getTs(a), getTs(b)))
  }

  const hBySuffix: Record<string, any[]> = {}
  for (const rec of handler) {
    const s = extractFileSuffix(rec.file_name)
    if (s) { if (!hBySuffix[s]) hBySuffix[s] = []; hBySuffix[s].push(rec) }
  }
  for (const s of Object.keys(hBySuffix)) {
    hBySuffix[s].sort((a: any, b: any) => compareTimestampsAsc(getTs(a), getTs(b)))
  }

  const sortedConsolidated = consolidated.slice().sort((a: any, b: any) => compareTimestampsDesc(getTs(a), getTs(b)))
  const iterations: Iteration[] = []
  const rawsUsedByConsolidation = new Set<number>()

  for (const consRec of sortedConsolidated) {
    const suffix = extractFileSuffix(consRec.file_name)
    const systems = extractSystemNames(consRec.file_name)
    const consTs = getTs(consRec)

    // Find the latest raw per system created at or before the consolidation
    const rawRecs: any[] = []
    for (const sysName of systems) {
      const match = allRaw.find(r =>
        normalizeKey(r.output_key_name || '') === normalizeKey(sysName) &&
        compareTimestampsAsc(getTs(r), consTs) <= 0
      )
      if (match) {
        rawRecs.push(match)
        rawsUsedByConsolidation.add(match.id)
      }
    }

    const suffixRMs = suffix ? (rmBySuffix[suffix] || []) : []
    const suffixHs = suffix ? (hBySuffix[suffix] || []) : []

    if (suffixRMs.length === 0) {
      // No RM records — iteration stopped at consolidation
    const records = { raw: rawRecs, consolidated: [consRec], 'rule-manager': [], handler: [] }
    iterations.push({
      start: getTs(consRec),
      label: formatTimestamp(getTs(consRec)),
      handlerTimestamp: getIterationHandlerTimestamp(records),
      forcedStages: new Set<string>(),
      records,
      controlResultId: getIterationControlResultId(records),
    })
    } else {
      // Each RM record = one iteration (first is normal, rest are forced RM)
      const usedHandlerIds = new Set<number>()

      for (let rmIdx = 0; rmIdx < suffixRMs.length; rmIdx++) {
        const rmRec = suffixRMs[rmIdx]
        const rmTs = getTs(rmRec)
        const nextRmTs = rmIdx < suffixRMs.length - 1 ? getTs(suffixRMs[rmIdx + 1]) : null

        // Find handlers created after this RM but before the next RM
        const matchedHandlers = suffixHs.filter(h => {
          if (usedHandlerIds.has(h.id)) return false
          const hTs = getTs(h)
          return compareTimestampsAsc(rmTs, hTs) <= 0 && (nextRmTs === null || compareTimestampsAsc(hTs, nextRmTs) < 0)
        })

        if (matchedHandlers.length <= 1) {
          // Normal case: 0 or 1 handler for this RM run
          const hRec = matchedHandlers[0] || null
          if (hRec) usedHandlerIds.add(hRec.id)

          const latestRec = hRec || rmRec
          const forced = new Set<string>()
          if (rmIdx > 0) forced.add('rule-manager')
          const records = { raw: rawRecs, consolidated: [consRec], 'rule-manager': [rmRec], handler: hRec ? [hRec] : [] }

          iterations.push({
            start: getTs(latestRec),
            label: formatTimestamp(getTs(latestRec)),
            handlerTimestamp: getIterationHandlerTimestamp(records),
            forcedStages: forced,
            records,
            controlResultId: getIterationControlResultId(records),
          })
        } else {
          // Multiple handlers for one RM run — first is normal, rest are forced handler
          for (let hIdx = 0; hIdx < matchedHandlers.length; hIdx++) {
            const hRec = matchedHandlers[hIdx]
            usedHandlerIds.add(hRec.id)

            const forced = new Set<string>()
            if (rmIdx > 0) forced.add('rule-manager')
            if (hIdx > 0) forced.add('handler')
            const records = { raw: rawRecs, consolidated: [consRec], 'rule-manager': [rmRec], handler: [hRec] }

            iterations.push({
              start: getTs(hRec),
              label: formatTimestamp(getTs(hRec)),
              handlerTimestamp: getIterationHandlerTimestamp(records),
              forcedStages: forced,
              records,
              controlResultId: getIterationControlResultId(records),
            })
          }
        }
      }
    }
  }

  // Detect forced consolidation from metadata_json.is_force_consolidation flag
  for (const iter of iterations) {
    const consRec = iter.records.consolidated[0]
    if (consRec?.metadata_json?.is_force_consolidation) {
      iter.forcedStages.add('consolidated')
    }
  }

  // Raw records not referenced by any consolidation — group into iterations by 24h window
  const orphanRaws = allRaw.filter(r => !rawsUsedByConsolidation.has(r.id))
  const orphanGroups: any[][] = []
  for (const rawRec of orphanRaws) {
    const ts = new Date(getTs(rawRec)).getTime()
    // Try to add to an existing group within 24 hours of the group's first record
    const group = orphanGroups.find(g =>
      Math.abs(new Date(getTs(g[0])).getTime() - ts) < 24 * 60 * 60 * 1000
    )
    if (group) {
      group.push(rawRec)
    } else {
      orphanGroups.push([rawRec])
    }
  }
  for (const group of orphanGroups) {
    const latest = group.reduce((a: any, b: any) => compareTimestampsDesc(getTs(a), getTs(b)) <= 0 ? a : b)
    const records = {
      raw: group,
      consolidated: [],
      'rule-manager': [],
      handler: [],
    }
    iterations.push({
      start: getTs(latest),
      label: formatTimestamp(getTs(latest)),
      handlerTimestamp: getIterationHandlerTimestamp(records),
      forcedStages: new Set<string>(),
      records,
      controlResultId: getIterationControlResultId(records),
    })
  }

  iterations.sort((a, b) => compareTimestampsDesc(a.start, b.start))
  return iterations
}

const normalizeSysName = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, '')

const buildVirtualIterations = (alerts: any[]): Iteration[] =>
  alerts
    .filter(a => VIRTUAL_ITERATION_ALERT_TYPES.has(a.alert_type) && (!a.status || a.status === 'open'))
    .map(a => ({
      start: a.created_at,
      label: formatTimestamp(a.created_at),
      handlerTimestamp: a.created_at,
      forcedStages: new Set<string>(),
      records: { raw: [], consolidated: [], 'rule-manager': [], handler: [] },
      isVirtual: true as const,
      virtualAlert: a,
    }))

export const buildPipelineDataForIteration = (iteration: Iteration | null, auditControl: any, hasExecutions: boolean, allExecutions: any[] = []): PipelineData => {
  if (iteration?.isVirtual && iteration.virtualAlert) {
    const alert = iteration.virtualAlert
    const alertSysNorm = normalizeSysName(alert.system_name || '')
    const recentRaws = allExecutions.filter((r: any) => r.stage === 'raw' && compareTimestampsAsc(getTs(r), alert.created_at) <= 0)
    const rawSources = buildSources(auditControl, [], recentRaws)
    const sources: PipelineSource[] = rawSources.map(s => {
      if (!alertSysNorm) return s
      const labelNorm = normalizeSysName(s.label)
      const abbrNorm = normalizeSysName(s.abbreviation || '')
      const matches = labelNorm.includes(alertSysNorm) || alertSysNorm.includes(labelNorm) ||
        (abbrNorm && (abbrNorm.includes(alertSysNorm) || alertSysNorm.includes(abbrNorm)))
      return matches ? { ...s, status: 'exception' as const, timestamp: alert.created_at } : s
    })
    const steps: PipelineStep[] = STEP_STAGES.map(stage => ({
      id: stage, label: STAGE_LABELS[stage], status: 'pending' as const,
      statusText: 'Not reached', timestamp: undefined, fileName: undefined,
    }))
    const logs: LogEntry[] = [{
      timestamp: formatTimestamp(alert.created_at),
      level: 'ERROR',
      message: alert.message || 'ETL job failed',
    }]
    return { steps, sources, logs, artifacts: [] }
  }

  const iterationByStage = iteration?.records || {}

  const rawRecords = iterationByStage['raw'] || []

  // Orphan iteration = a re-run that hasn't been consolidated yet. In that case the iteration's
  // raws only cover the re-run system(s), so for the other systems in the control fall back to
  // their most recent raw within 24h of this iteration's start.
  const isOrphan = rawRecords.length > 0 && (iterationByStage['consolidated'] || []).length === 0
  let fallbackRaws: any[] = []
  if (isOrphan && iteration) {
    const iterStartMs = new Date(iteration.start).getTime()
    const iterationRawIds = new Set(rawRecords.map((r: any) => r.id))
    const ONE_DAY_MS = 24 * 60 * 60 * 1000
    fallbackRaws = allExecutions.filter((r: any) => {
      if (r.stage !== 'raw') return false
      if (iterationRawIds.has(r.id)) return false
      const ts = new Date(getTs(r)).getTime()
      return !Number.isNaN(ts) && iterStartMs - ts >= 0 && iterStartMs - ts <= ONE_DAY_MS
    })
  }
  const sources = buildSources(auditControl, rawRecords, fallbackRaws)

  const steps: PipelineStep[] = STEP_STAGES.map(stage => {
    const records = iterationByStage[stage] || []
    const errors = records.filter(hasError)
    let status: PipelineStep['status'] = 'pending'
    let statusText = 'No records'
    if (records.length > 0 && errors.length > 0) {
      status = stage === 'handler' ? 'success' : 'exception'
      statusText = `${errors.length} error${errors.length !== 1 ? 's' : ''}`
    } else if (records.length > 0) {
      status = stage === 'handler' ? 'success' : 'completed'
      statusText = `${records.length} file${records.length !== 1 ? 's' : ''}`
    }
    const latest = records.length > 0
      ? records.reduce((a: any, b: any) => compareTimestampsDesc(getTs(a), getTs(b)) <= 0 ? a : b)
      : null
    return {
      id: stage,
      label: STAGE_LABELS[stage],
      status,
      statusText,
      timestamp: latest ? getTs(latest) : undefined,
      fileName: latest?.file_name || (records.length > 0 ? records[0].file_name : undefined),
    }
  })

  const logs: LogEntry[] = []
  for (const stage of ['raw', ...STEP_STAGES]) {
    const records = iterationByStage[stage] || []
    for (const rec of records) {
      if (hasError(rec)) {
        const rawError = String(rec.metadata_json.error || '')
        const stageLabel = STAGE_LABELS[stage] || 'Raw'
        const isDbError = /InterfaceError|network error on read|pg8000|psycopg|OperationalError|connection.*closed|timeout/i.test(rawError)
        const message = isDbError && stage === 'handler'
          ? `[${stageLabel}] Failed to write results to the database. Please re-trigger the handler.`
          : `[${stageLabel}] ${rawError}`
        logs.push({ timestamp: '', level: 'ERROR', message })
      }
    }
  }
  if (logs.length === 0 && hasExecutions) {
    logs.push({ timestamp: '', level: 'INFO', message: 'No errors detected in this iteration.' })
  }
  if (!hasExecutions) {
    logs.push({ timestamp: '', level: 'INFO', message: 'No execution records found.' })
  }

  const iterationRecords = Object.values(iterationByStage).flat()
  const getFileType = (fileName: string): Artifact['type'] => {
    if (fileName.endsWith('.pdf')) return 'pdf'
    if (fileName.endsWith('.csv')) return 'csv'
    return 'json'
  }

  const artifacts = iterationRecords
    .filter((rec: any) => rec.file_name)
    .map((rec: any) => ({
      fileName: rec.file_name,
      fileSize: rec.stage,
      type: getFileType(rec.file_name),
    }))

  return { steps, sources, logs, artifacts }
}

const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const IconExclamation = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="9" x2="12" y2="13" />
    <circle cx="12" cy="17" r="0.5" fill="#fff" />
  </svg>
)

const IconClock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

const IconFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
)

const IconPeople = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const IconTicket = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 10h20" />
  </svg>
)

const IconFilePdf = () => (
  <span className="artifact-type-badge artifact-type-pdf">PDF</span>
)
const IconFileCsv = () => (
  <span className="artifact-type-badge artifact-type-csv">CSV</span>
)
const IconFileJson = () => (
  <span className="artifact-type-badge artifact-type-json">&lt; &gt;</span>
)

const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const IconPlay = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
)

const sourceIcons: Record<string, React.FC> = {
  filter: IconFilter,
  people: IconPeople,
  ticket: IconTicket,
  cloud: IconTicket,
  database: IconTicket,
}

const getStepIcon = (step: PipelineStep) => {
  switch (step.status) {
    case 'completed': return <IconCheck />
    case 'success': return <IconCheck />
    case 'exception': return <IconExclamation />
    default: return <IconClock />
  }
}


const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
)

const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 6 15 12 9 18" />
  </svg>
)

const ExecutionPipeline: React.FC<ExecutionPipelineProps> = ({
  auditControlId: rawAcId,
  auditControl,
  companyName,
  onDataChange,
  siblingControls = [],
}) => {
  const auditControlId = String(rawAcId)
  const { data: glueSchedules } = useCachedFetch('glue-triggers', async () => {
    const result = await newHttpService.get('/api/backoffice/glue-schedules')
    return result.schedules as any[]
  })
  const glueAwsRegion: string | undefined = (glueSchedules as any[])?.[0]?.aws_region
  const entityName = auditControl?.audit?.entity?.name || ''
  const navigate = useNavigate()

  const { data: acAlerts } = useCachedFetch(
    `alerts-ac-${auditControlId}`,
    () => newHttpService.get('/api/backoffice/alert-events', { audit_control_id: auditControlId }) as Promise<any[]>
  )
  const { data: entityAlerts } = useCachedFetch(
    entityName ? `alerts-entity-${entityName}` : null,
    () => newHttpService.get('/api/backoffice/alert-events', { entity_name: entityName }) as Promise<any[]>
  )

  const acSystemNames = useMemo(() => {
    const names = new Set<string>()
    for (const key of ['mainSystem', 'hrSystem', 'ticketSystem']) {
      const name = auditControl?.control?.[key]?.system?.name
      if (name) names.add(name.toLowerCase())
    }
    return names
  }, [auditControl])

  const allAlerts = useMemo(() => {
    const direct = (acAlerts as any[]) || []
    const indirect = ((entityAlerts as any[]) || []).filter((a: any) =>
      a.system_name &&
      acSystemNames.has(a.system_name.toLowerCase()) &&
      a.audit_control_id !== Number(auditControlId)
    )
    const seen = new Set<number>()
    const merged: any[] = []
    for (const a of [...direct, ...indirect]) {
      if (!seen.has(a.id)) { seen.add(a.id); merged.push(a) }
    }
    return merged
  }, [acAlerts, entityAlerts, acSystemNames, auditControlId])

  const [iterationIndex, setIterationIndex] = useState(0)
  const [executions, setExecutions] = useState<any[]>([])
  const [isLoadingExec, setIsLoadingExec] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [triggeringStage, setTriggeringStage] = useState<string | null>(null)
  const [triggerResult, setTriggerResult] = useState<{ success: boolean; message: string; details?: string[] } | null>(null)
  const [pendingTrigger, setPendingTrigger] = useState<{ preview: any; payload: any; label: string; hasGenericJobs?: boolean } | null>(null)
  const [consolidationWarning, setConsolidationWarning] = useState<{ triggeredLabel: string; staleSystems: string[] } | null>(null)
  const [genericAcIdOverride, setGenericAcIdOverride] = useState<string>('')
  const [isOutdatedFileAllowed, setIsOutdatedFileAllowed] = useState(false)
  const [activateAcFirst, setActivateAcFirst] = useState(false)
  const [activeJobRuns, _setActiveJobRuns] = useState<JobRunStatus[]>(() => jobRunStore.get(auditControlId) || [])
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>(() => activityLogStore.get(auditControlId) || [])
  const [lambdaLogsByRunId, setLambdaLogsByRunId] = useState<Map<string, { logs: any[] | null; loading: boolean; logGroup?: string }>>(() => {
    const initial = new Map<string, { logs: any[] | null; loading: boolean; logGroup?: string }>()
    lambdaLogStore.forEach((v, k) => initial.set(k, { logs: v.logs, loading: false, logGroup: v.logGroup }))
    return initial
  })
  const [expandedLogPanelByRunId, setExpandedLogPanelByRunId] = useState<Set<string>>(new Set())
  const [expandedStreamsByRunId, setExpandedStreamsByRunId] = useState<Map<string, Set<string>>>(new Map())

  // The shared store is the source of truth for both modal and table.
  const setActiveJobRuns = useCallback((update: JobRunStatus[] | ((prev: JobRunStatus[]) => JobRunStatus[])) => {
    const current = jobRunStore.get(auditControlId) || []
    setStoreRuns(auditControlId, typeof update === 'function' ? update(current) : update)
  }, [auditControlId])

  useEffect(() => {
    const syncFromStore = () => {
      _setActiveJobRuns(jobRunStore.get(auditControlId) || [])
      setActivityLog([...(activityLogStore.get(auditControlId) || [])])
    }
    jobRunListeners.add(syncFromStore)
    syncFromStore()
    const stopMonitoring = jobRunMonitor.start()
    return () => { jobRunListeners.delete(syncFromStore); stopMonitoring() }
  }, [auditControlId])

  const appendLog = useCallback((type: ActivityLogEntry['type'], message: string, source?: string, job_run_id?: string, aws_region?: string) => {
    const entry: ActivityLogEntry = { time: nowTime(), type, message, source, job_run_id, aws_region }
    addLogEntry(auditControlId, entry)
    setActivityLog(activityLogStore.get(auditControlId) || [])
  }, [auditControlId])

  const dismissRunGroup = useCallback((entries: ActivityLogEntry[]) => {
    const keys = new Set(entries.map(e => `${e.time}|${e.source}|${e.message}`))
    const current = activityLogStore.get(auditControlId) || []
    const updated = current.filter(e => !keys.has(`${e.time}|${e.source}|${e.message}`))
    activityLogStore.set(auditControlId, updated)
    saveToLS(LS_LOG_KEY, activityLogStore)
    setActivityLog(updated)
  }, [auditControlId])

  // No auto-scroll — the activity log is at the bottom and scrollIntoView would jump the page

  const previousRunStates = useRef(new Map(activeJobRuns.filter(isRealGlueRun).map(run => [jobRunKey(run), run.state])))
  useEffect(() => {
    const realRuns = activeJobRuns.filter(isRealGlueRun)
    const completed = realRuns.some(run => {
      const previous = previousRunStates.current.get(jobRunKey(run))
      return previous && !TERMINAL_STATES.includes(previous) && TERMINAL_STATES.includes(run.state)
    })
    if (completed) setFetchKey(key => key + 1)
    previousRunStates.current = new Map(realRuns.map(run => [jobRunKey(run), run.state]))
  }, [activeJobRuns])

  useEffect(() => {
    if (!auditControlId) return
    setIsLoadingExec(true)
    newHttpService.get('/api/backoffice/execution-history', { audit_control_id: auditControlId, ...(companyName ? { company_name: companyName } : {}) })
      .then((data: any[]) => { setExecutions(data) })
      .catch(() => setExecutions([]))
      .finally(() => setIsLoadingExec(false))
  }, [auditControlId, fetchKey, companyName])

  const refresh = () => setFetchKey(k => k + 1)

  const triggerStage = async (stage: string, inputFile?: string, systemName?: string | string[], overrideAcId?: number) => {
    const stageLabels: Record<string, string> = {
      'raw': 'ETL (Raw)',
      'consolidation': 'Consolidation',
      'rule-manager': 'Rule Manager',
      'handler': 'Handler',
    }
    const displayName = Array.isArray(systemName) ? systemName[0] : systemName
    const label = displayName ? `${displayName} ETL` : (stageLabels[stage] || stage)

    setTriggeringStage(displayName ? `raw-${displayName}` : stage)
    setTriggerResult(null)
    try {
      const payload: any = { stage, audit_control_id: Number(auditControlId), dry_run: true, ...(companyName ? { company_name: companyName } : {}) }
      if (inputFile) payload.input_file = inputFile
      if (systemName) payload.system_name = systemName
      if (overrideAcId) payload.override_audit_control_id = overrideAcId
      const preview = await newHttpService.post('/api/backoffice/trigger-stage', payload)

      if (preview.dry_run) {
        if (!preview.actions?.length) {
          setTriggerResult({ success: false, message: preview.message || `No actions found for ${label}` })
          setTriggeringStage(null)
          return
        }
        // Show confirmation modal with preview data — clear triggering state so the button is clickable
        setTriggeringStage(null)
        const hasGenericJobs = (preview.actions || []).some((a: any) => a.is_generic)
        setPendingTrigger({ preview, payload, label, hasGenericJobs })
        setGenericAcIdOverride('')
        setActivateAcFirst(false)
      } else {
        // Backend doesn't support dry_run — already executed
        setTriggerResult({
          success: !preview.errors?.length,
          message: preview.message || `${label} triggered`,
          details: preview.function_name ? [`Function: ${preview.function_name}`] : undefined,
        })
        setTriggeringStage(null)
      }
    } catch (err: any) {
      setTriggerResult({ success: false, message: err?.message || `Failed to trigger ${label}` })
      setTriggeringStage(null)
    }
  }

  const confirmTrigger = async () => {
    if (!pendingTrigger) return
    const { payload, label } = pendingTrigger
    const acIdOverride = genericAcIdOverride.trim()
    setPendingTrigger(null)
    setGenericAcIdOverride('')
    setTriggeringStage(payload.stage)
    const logMessage = acIdOverride ? `Triggered ETL with Audit Control ID ${acIdOverride}` : 'Triggered ETL'

    const uiTriggeredAt = Date.now()
    // Show running animation immediately, before API responds
    setActiveJobRuns(prev => [...prev, {
      job_name: `pending-${label}`,
      job_run_id: `pending-${Date.now()}`,
      system_name: label,
      state: 'RUNNING' as const,
    }])

    try {
      if (activateAcFirst && payload.stage === 'raw') {
        const mutation = updateAuditControlMutation()
        await newHttpService.graphql(companyName || 'auditech', { query: mutation, variables: { auditControlId: Number(auditControlId), isAutomated: true } })
        setActivateAcFirst(false)
      }
      payload.dry_run = false
      if (acIdOverride) payload.override_audit_control_id = acIdOverride
      if (payload.stage === 'raw') payload.is_outdated_data_allowed = isOutdatedFileAllowed
      setIsOutdatedFileAllowed(false)
      const res = await newHttpService.post('/api/backoffice/trigger-stage', payload)

      // Handle ETL triggers — triggered is now an array of {job_name, job_run_id, system_name}
      if (res.triggered?.length) {
        const newRuns: JobRunStatus[] = res.triggered
          .filter((t: any) => typeof t === 'object' && t.job_run_id)
          .map((t: any) => ({
            job_name: t.job_name,
            job_run_id: t.job_run_id,
            system_name: t.system_name || t.job_name,
            state: 'STARTING' as const,
            ui_stage: 'raw',
            ui_triggered_at: uiTriggeredAt,
            aws_region: res.aws_region || glueAwsRegion,
          }))
        for (const run of newRuns) {
          appendLog('trigger', logMessage, run.system_name, run.job_run_id, run.aws_region || glueAwsRegion)
          appendLog('status', 'STARTING', run.system_name, run.job_run_id, run.aws_region)
        }
        if (newRuns.length > 0) {
          setActiveJobRuns(prev => [...prev.filter(r => !r.job_run_id.startsWith('pending-')), ...newRuns])

          // When "Run all audit controls" is selected (no override), broadcast the runs
          // to sibling ACs so they also show the running indicator and activity log.
          if (!acIdOverride && siblingControls.length > 0) {
            for (const sibling of siblingControls) {
              const matchingRuns = newRuns.filter(run => sibling.systems.some(system => systemsMatch(system, run.system_name)))
              if (matchingRuns.length === 0) continue
              const existing = jobRunStore.get(sibling.id) || []
              setStoreRuns(sibling.id, [...existing, ...matchingRuns])
              for (const run of matchingRuns) {
                addLogEntry(sibling.id, { time: nowTime(), type: 'trigger', message: logMessage, source: run.system_name })
                addLogEntry(sibling.id, { time: nowTime(), type: 'status', message: 'STARTING', source: run.system_name })
              }
            }
          }
        }
      }

      // Log errors and show feedback banner
      if (res.errors?.length) {
        for (const e of res.errors) {
          appendLog('error', e.error, e.job_name)
        }
        setTriggerResult({ success: false, message: res.message || `${label} had errors` })
      }

      // Lambda invocations (consolidation, rule-manager, handler)
      if (res.function_name) {
        const lambdaStatus = res.lambda_status // 'success' | 'error' | 'running' (timed out)
        const lambdaRunId = `lambda-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        appendLog('trigger', `Triggered ${label}`, label, lambdaRunId)

        let logType: ActivityLogEntry['type'] = 'complete'
        let logMessage = 'Completed successfully'
        let jobState: JobRunStatus['state'] = 'SUCCEEDED'

        if (lambdaStatus === 'error') {
          logType = 'error'
          const rawError = res.lambda_error || res.message || 'Failed'
          logMessage = rawError.startsWith('[ERROR]') ? rawError : `[ERROR] ${rawError}`
          jobState = 'FAILED'
        } else if (lambdaStatus === 'running') {
          logType = 'status'
          logMessage = 'Still running (waiting for result...)'
          jobState = 'RUNNING'
        }

        appendLog(logType, logMessage, label, lambdaRunId)
        setActiveJobRuns(prev => [...prev.filter(r => !r.job_run_id.startsWith('pending-')), {
          job_name: res.function_name,
          job_run_id: lambdaRunId,
          system_name: label,
          state: jobState,
          ui_stage: payload.stage,
          ui_triggered_at: uiTriggeredAt,
        }])
        if (['consolidation', 'rule-manager', 'handler'].includes(payload.stage)) {
          // Use Lambda invocation tail logs (captured via LogType="Tail") as the immediate result.
          // Then try to enrich with the last 5 CloudWatch streams; fall back to tail logs on failure.
          const logGroup = `/aws/lambda/${res.function_name}`
          const tailEntry = (res.lambda_logs as string[] | undefined)?.length
            ? [{ stream_name: 'Latest Run', last_event_timestamp: Date.now(), events: (res.lambda_logs as string[]).map((msg: string) => ({ message: msg })) }]
            : null
          setLambdaLogsByRunId(prev => new Map(prev).set(lambdaRunId, { logs: tailEntry, loading: true, logGroup }))
          newHttpService.get('/api/backoffice/lambda-logs', { function_name: res.function_name, limit: 5 })
            .then((history: any) => {
              const streams: any[] = Array.isArray(history) ? history : []
              const logs = streams.length ? streams : (tailEntry ?? [])
              lambdaLogStore.set(lambdaRunId, { logs, logGroup })
              saveToLS(LS_LAMBDA_LOGS_KEY, lambdaLogStore)
              setLambdaLogsByRunId(prev => {
                const next = new Map(prev)
                next.set(lambdaRunId, { logs, loading: false, logGroup })
                return next
              })
            })
            .catch(() => {
              const logs = tailEntry ?? []
              lambdaLogStore.set(lambdaRunId, { logs, logGroup })
              saveToLS(LS_LAMBDA_LOGS_KEY, lambdaLogStore)
              setLambdaLogsByRunId(prev => {
                const next = new Map(prev)
                next.set(lambdaRunId, { logs, loading: false, logGroup })
                return next
              })
            })
        }
        // Terminal Lambda — refresh execution history to show the new records
        if (jobState !== 'RUNNING') {
          setTimeout(() => refresh(), 3000)
          // Handler writes execution_history async via trigger-manager; give it extra time
          // then update the health indicator in the parent table
          if (payload.stage === 'handler') {
            setTimeout(() => onDataChange?.(), 8000)
          }
        }
        // If the Lambda is still running (timed out waiting), auto-resolve after 60s
        if (jobState === 'RUNNING') {
          setTimeout(() => {
            setActiveJobRuns(prev => prev.map(r =>
              r.job_run_id === lambdaRunId ? { ...r, state: 'STOPPED' as const } : r
            ))
            appendLog('complete', 'Lambda timed out — check execution history for results', label)
            // Handler may have finished writing by now even if we timed out waiting
            if (payload.stage === 'handler') onDataChange?.()
          }, 60000)
        }
      }

      // If an ETL (raw) was triggered and one or more required-daily systems have not had
      // a normalized execution within the last 12h, consolidation will not auto-run.
      // In that case we tell the user to trigger consolidation manually, mirroring the lambda gate.
      if (payload.stage === 'raw' && res.triggered?.length) {
        const now = Date.now()
        const triggeredSystemNames = Array.isArray(payload.system_name)
          ? payload.system_name
          : payload.system_name
            ? [payload.system_name]
            : []
        const staleSystems = getManualConsolidationWarningSystems(auditControl, executions, now, triggeredSystemNames)
        if (staleSystems.length > 0) {
          setConsolidationWarning({ triggeredLabel: label, staleSystems })
        }
      }
    } catch (err: any) {
      appendLog('error', err?.message || 'Failed', label)
      setTriggerResult({ success: false, message: err?.message || `Failed to trigger ${label}` })
    } finally {
      setTriggeringStage(null)
      // Clean up any remaining placeholder runs (edge case: 200 with no triggered/function_name)
      setActiveJobRuns(prev => prev.filter(r => !r.job_run_id.startsWith('pending-')))
    }
  }

  const cancelTrigger = () => {
    setPendingTrigger(null)
    setTriggeringStage(null)
    setIsOutdatedFileAllowed(false)
  }

  const [downloadingFile, setDownloadingFile] = useState<string | null>(null)

  const downloadArtifact = async (fileKey: string) => {
    setDownloadingFile(fileKey)
    try {
      const res = await newHttpService.get('/api/backoffice/download-artifact', {
        audit_control_id: auditControlId,
        file_key: fileKey,
        ...(companyName ? { company_name: companyName } : {}),
      })
      if (res.url) {
        const a = document.createElement('a')
        a.href = res.url
        a.download = fileKey.split('/').pop() || fileKey
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    } catch (err) {
      console.error('Failed to download artifact:', err)
      setTriggerResult({ success: false, message: `Failed to download ${fileKey.split('/').pop()}` })
    } finally {
      setDownloadingFile(null)
    }
  }

  const [wpStatus, setWpStatus] = useState<'idle' | 'generating' | 'success' | 'error'>('idle')
  const [isHistoricalWpOpen, setIsHistoricalWpOpen] = useState(false)
  const wpPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const generateWorkingPaper = async (controlResultIdOverride?: number): Promise<boolean> => {
    if (!hasAnyHandlerHistory) {
      setWpStatus('error')
      setTriggerResult({ success: false, message: 'No handler session exists yet for Working Paper + IPEs download' })
      setTimeout(() => setWpStatus('idle'), 5000)
      return false
    }
    setWpStatus('generating')
    try {
      const res = await newHttpService.post('/api/backoffice/generate-working-paper', {
        audit_control_id: auditControlId,
        ...(controlResultIdOverride || workingPaperControlResultId ? { control_result_id: controlResultIdOverride || workingPaperControlResultId } : {}),
        output_type: 'zip',
        ...(companyName ? { company_name: companyName } : {}),
      })
      const notificationId = res.notification_id
      const wpCompanyName = res.company_name
      if (!notificationId || !wpCompanyName) {
        setWpStatus('error')
        setTriggerResult({ success: false, message: 'Failed to start working paper generation' })
        return false
      }

      // Poll notification status every 3 seconds
      wpPollRef.current = setInterval(async () => {
        try {
          const status = await newHttpService.get('/api/backoffice/working-paper-status', {
            notification_id: String(notificationId),
            company_name: wpCompanyName,
          })
          if (status.status === 'SUCCESS' || status.status === 'WARNING') {
            if (wpPollRef.current) clearInterval(wpPollRef.current)
            wpPollRef.current = null
            setWpStatus('success')
            let fileName = `working_paper_${auditControlId}.zip`
            if (status.url) {
              // The WP generator returns the presigned URL directly in response_data
              const url = typeof status.url === 'string' ? status.url : ''
              if (url) {
                try {
                  const urlPath = new URL(url).pathname
                  const s3FileName = decodeURIComponent(urlPath.split('/').pop() || '')
                  if (s3FileName) fileName = s3FileName
                } catch { /* use default */ }
                const a = document.createElement('a')
                a.href = url
                a.download = fileName
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
              }
            }
            setTriggerResult({ success: true, message: status.status === 'WARNING' ? 'Working paper generated with warnings' : `Downloaded: ${fileName}` })
            setTimeout(() => setWpStatus('idle'), 3000)
          } else if (status.status === 'ERROR') {
            if (wpPollRef.current) clearInterval(wpPollRef.current)
            wpPollRef.current = null
            setWpStatus('error')
            setTriggerResult({ success: false, message: 'Working paper generation failed' })
            setTimeout(() => setWpStatus('idle'), 5000)
          }
        } catch {
          // Keep polling on network errors
        }
      }, 3000)
      return true
    } catch (err) {
      console.error('Failed to generate working paper:', err)
      setWpStatus('error')
      setTriggerResult({ success: false, message: 'Failed to start working paper generation' })
      setTimeout(() => setWpStatus('idle'), 5000)
      return false
    }
  }

  // Clean up WP polling on unmount
  useEffect(() => {
    return () => {
      if (wpPollRef.current) clearInterval(wpPollRef.current)
    }
  }, [])

  // Auto-dismiss trigger feedback after 8s
  useEffect(() => {
    if (!triggerResult) return
    const timer = setTimeout(() => setTriggerResult(null), 8000)
    return () => clearTimeout(timer)
  }, [triggerResult])

  const iterations = useMemo(() => {
    const real = buildIterations(executions)
    const virtual = buildVirtualIterations(allAlerts)
    const merged = [...real, ...virtual]
    merged.sort((a, b) => compareTimestampsDesc(a.start, b.start))
    return merged
  }, [executions, allAlerts])

  const iterationAlerts = useMemo(() =>
    [...allAlerts]
      .filter((a: any) => !VIRTUAL_ITERATION_ALERT_TYPES.has(a.alert_type))
      .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  , [allAlerts])

  const currentIteration = iterations[iterationIndex] || null
  const currentHandlerTimestamp = currentIteration?.handlerTimestamp
  const currentIterationControlResultId = currentIteration?.controlResultId
  const hasAnyHandlerHistory = executions.some((rec: any) => rec.stage === 'handler')
  const latestSuccessfulHandlerIteration = useMemo(
    () => iterations.find(iter => {
      const handlerRecs = iter.records.handler || []
      return handlerRecs.length > 0 && !handlerRecs.some(hasError)
    }) || null,
    [iterations]
  )

  const latestWorkingPaperIteration = useMemo(() => {
    if (currentHandlerTimestamp) {
      const alignedIteration = iterations.find(iter => !!iter.controlResultId && iter.handlerTimestamp === currentHandlerTimestamp)
      if (alignedIteration) return alignedIteration
    }
    return iterations.find(iter => !!iter.controlResultId) || null
  }, [iterations, currentHandlerTimestamp])

  const workingPaperIteration = latestWorkingPaperIteration
  const workingPaperControlResultId = workingPaperIteration?.controlResultId
  const workingPaperSourceIteration = workingPaperControlResultId ? workingPaperIteration : latestSuccessfulHandlerIteration
  const isWorkingPaperAligned = !!workingPaperIteration?.controlResultId
    && currentIterationControlResultId !== undefined
    && workingPaperIteration.controlResultId === currentIterationControlResultId
  const currentSessionHasControlResultId = currentIterationControlResultId !== undefined
  const workingPaperSourceDateTime = workingPaperSourceIteration?.handlerTimestamp || workingPaperSourceIteration?.start
  const currentSessionDateTime = currentHandlerTimestamp || currentIteration?.start
  const isWorkingPaperSameDayAsCurrentSession = !!currentSessionDateTime
    && !!workingPaperSourceDateTime
    && isSameCalendarDay(currentSessionDateTime, workingPaperSourceDateTime)
  const historicalWpInitialDate = currentHandlerTimestamp || currentIteration?.start || workingPaperSourceDateTime || new Date().toISOString()
  const data: PipelineData = useMemo(
    () => buildPipelineDataForIteration(currentIteration, auditControl, executions.length > 0, executions),
    [currentIteration, auditControl, executions]
  )

  useEffect(() => {
    setIterationIndex(0)
  }, [auditControlId])

  const totalIterations = iterations.length
  const canGoNewer = iterationIndex > 0
  const canGoOlder = iterationIndex < totalIterations - 1

  // Find the index of the latest successful iteration (handler completed without errors)
  const lastSuccessIndex = useMemo(() => {
    return iterations.findIndex(iter => {
      const handlerRecs = iter.records['handler'] || []
      return handlerRecs.length > 0 && !handlerRecs.some(hasError)
    })
  }, [iterations])

  if (isLoadingExec) {
    return (
      <div className="execution-pipeline">
        <div className="pipeline-loading">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="spinning">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          <span>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="execution-pipeline">
      <span className="section-header pipeline-main-title">EXECUTION PIPELINE</span>
      <div className="pipeline-body">
        <div className="pipeline-left">
          <div className="pipeline-header">
            {totalIterations > 0 && (
              <div className="iteration-nav">
                <button
                  className="iter-btn"
                  disabled={!canGoOlder}
                  onClick={() => setIterationIndex(i => i + 1)}
                  aria-label="Older iteration"
                >
                  <IconChevronLeft />
                </button>
                <span className="iter-label">
                  {currentIteration?.isVirtual && <span className="iter-virtual-badge">FAILED</span>}
                  {currentIteration?.label || ''} ({iterationIndex + 1}/{totalIterations})
                </span>
                <button
                  className="iter-btn"
                  disabled={!canGoNewer}
                  onClick={() => setIterationIndex(i => i - 1)}
                  aria-label="Newer iteration"
                >
                  <IconChevronRight />
                </button>
                {lastSuccessIndex >= 0 && (
                  <button
                    className={`iter-jump-btn ${lastSuccessIndex === iterationIndex ? 'active' : ''}`}
                    disabled={lastSuccessIndex === iterationIndex}
                    onClick={() => setIterationIndex(lastSuccessIndex)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Last Success
                  </button>
                )}
                <button className="refresh-btn" onClick={refresh} disabled={isLoadingExec} aria-label="Refresh pipeline">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={isLoadingExec ? 'spinning' : ''}>
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {triggerResult && (
            <div className={`trigger-feedback trigger-feedback-${triggerResult.success ? 'success' : 'error'}`}>
              <div className="trigger-feedback-body">
                <span className="trigger-feedback-message">{triggerResult.message}</span>
                {triggerResult.details && (
                  <div className="trigger-feedback-details">
                    {triggerResult.details.map((d, i) => <span key={i}>{d}</span>)}
                  </div>
                )}
              </div>
              <button className="trigger-feedback-close" onClick={() => setTriggerResult(null)}>&times;</button>
            </div>
          )}
          <div className="pipeline-run-overview">
          <div className="pipeline-viz">
          <div className="pipeline-sources">
            {data.sources.map((source, i) => {
              const SourceIcon = sourceIcons[source.icon] || IconFilter
              const sourceName = source.label.toLowerCase()
              const sourceAbbr = source.abbreviation?.toLowerCase()
              const isSourceRunning =
                triggeringStage === `raw-${source.label}` ||
                activeJobRuns.some(r =>
                  !TERMINAL_STATES.includes(r.state) && (
                    r.system_name === source.label ||
                    r.system_name.toLowerCase() === sourceName ||
                    (sourceAbbr && r.system_name.toLowerCase() === sourceAbbr)
                  )
                )
              const scheduledJobs = glueSchedules && entityName ? getSystemScheduledJobs(entityName, source.label, source.abbreviation, glueSchedules) : []
              const hasSchedule = !glueSchedules || !entityName || systemHasSchedule(entityName, source.label, source.abbreviation, glueSchedules)
              return (
                <div key={i} className={`source-item source-${isSourceRunning ? 'running' : (source.status || 'pending')}${hasSchedule ? ' triggerable' : ''}`} title={source.fileName || ''}>
                  <div className="source-icon-wrapper">
                    {isSourceRunning
                      ? <span className="source-icon source-spinner" />
                      : <span className="source-icon"><SourceIcon /></span>
                    }
                    {hasSchedule && <button
                      className="source-trigger-overlay"
                      disabled={triggeringStage !== null}
                      onClick={(e) => { e.stopPropagation(); triggerStage('raw', undefined, [source.label, source.abbreviation].filter(Boolean) as string[]) }}
                      title={`Trigger ETL for ${source.label}`}
                    >
                      <IconPlay />
                    </button>}
                  </div>
                  <div className="source-info">
                    <span className="source-label">{source.label}</span>
                    <span className="source-type">{source.systemName || (source.type === 'input' ? 'In Scope' : 'Source')}</span>
                    {source.timestamp && <span className="source-timestamp">{formatTimestamp(source.timestamp)}</span>}
                  </div>
                  <AwsEtlLinks jobs={scheduledJobs} sourceName={source.label} />
                </div>
              )
            })}
          </div>

          <div className="pipeline-connector-horizontal" />

          <div className="pipeline-steps">
            {data.steps.map((step, i) => {
              const handleStepTrigger = () => {
                if (step.id === 'consolidated') {
                  triggerStage('consolidation')
                } else if (step.id === 'rule-manager') {
                  const consFile = currentIteration?.records?.consolidated?.[0]?.file_name
                  if (!consFile) {
                    setTriggerResult({ success: false, message: 'No consolidated file available in this iteration' })
                    return
                  }
                  triggerStage('rule-manager', consFile)
                } else if (step.id === 'handler') {
                  const rmFile = currentIteration?.records?.['rule-manager']?.[0]?.file_name
                  if (!rmFile) {
                    setTriggerResult({ success: false, message: 'No Rule Manager output file available in this iteration' })
                    return
                  }
                  triggerStage('handler', rmFile)
                }
              }

              return (
                <React.Fragment key={step.id}>
                  <div className={`pipeline-step step-${step.status} triggerable`} title={step.fileName || ''}>
                    <div className="step-circle-wrapper">
                      <div className="step-circle">
                        {triggeringStage === step.id
                          ? <span className="dl-spinner dl-spinner-sm step-triggering-spinner" />
                          : getStepIcon(step)}
                      </div>
                      <button
                        className="step-trigger-overlay"
                        disabled={triggeringStage !== null}
                        onClick={handleStepTrigger}
                        title={`Trigger ${step.label}`}
                      >
                        <IconPlay />
                      </button>
                    </div>
                    <div className="step-label">{step.label}</div>
                    {step.timestamp && <div className="step-timestamp">{formatTimestamp(step.timestamp)}</div>}
                    {currentIteration?.forcedStages.has(step.id) && (
                      <div className="step-forced-label">Manually Triggered</div>
                    )}
                  </div>
                  {i < data.steps.length - 1 && <div className="step-line" />}
                </React.Fragment>
              )
            })}
          </div>
          </div>

          {currentIteration && <SessionFindings current={currentIteration} />}
          </div>

          {data.logs.some(l => l.level === 'ERROR') && (
            <div className="pipeline-errors">
              <div className="pipeline-errors-icon"><IconExclamation /></div>
              <div className="pipeline-errors-list">
                {data.logs.filter(l => l.level === 'ERROR').map((log, i) => (
                  <div key={i} className="pipeline-error-line">
                    {log.timestamp && <span className="error-timestamp">{log.timestamp}</span>}
                    {currentIteration?.isVirtual && currentIteration.virtualAlert?.system_name && (() => {
                      const raw = currentIteration.virtualAlert.system_name.toLowerCase()
                      const match = ['mainSystem', 'hrSystem', 'ticketSystem']
                        .map((k: string) => auditControl?.control?.[k]?.system?.name)
                        .find((n: string) => n && n.toLowerCase() === raw)
                      return <span className="error-system-label">{match ?? currentIteration.virtualAlert.system_name}</span>
                    })()}
                    <span className="error-message">{log.message}</span>
                  </div>
                ))}
                {currentIteration?.isVirtual && currentIteration.virtualAlert && (
                  <button
                    className="pipeline-error-health-link"
                    onClick={() => window.open(`/monitor?pair=${encodeURIComponent(buildPairKey(currentIteration.virtualAlert.entity_name, currentIteration.virtualAlert.system_name))}`, '_blank')}
                  >
                    View in Operations Monitor →
                  </button>
                )}
              </div>
            </div>
          )}

          {iterationAlerts.length > 0 && (
            <div className="pipeline-alert-events">
              <span className="section-header pae-header">ALERT EVENTS</span>
              <div className="pae-list">
                {iterationAlerts.map((alert: any) => {
                  const pairKey = buildPairKey(alert.entity_name, alert.system_name)
                  return (
                    <div
                      key={alert.id}
                      className={`pae-row pae-row--${(alert.severity || 'error').toLowerCase()} pae-row--clickable`}
                      onClick={() => navigate(`/monitor?pair=${encodeURIComponent(pairKey)}`)}
                      title="View in Operations Monitor"
                    >
                      <span className="pae-type">{ALERT_TYPE_LABELS[alert.alert_type] ?? alert.alert_type}</span>
                      {alert.system_name && <span className="pae-system">{alert.system_name}</span>}
                      <span className="pae-message" title={alert.message ?? ''}>{alert.message ?? '—'}</span>
                      <span className={`pae-status pae-status--${alert.status ?? 'open'}`}>{alert.status ?? 'open'}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {activityLog.length > 0 && (() => {
            // Split entries into per-run cards: each 'trigger' entry starts a new card.
            // Non-trigger entries without a preceding trigger fall into an implicit first card.
            const runGroups: RunGroup[] = []
            const sourceCount: Record<string, number> = {}
            for (const entry of activityLog) {
              if (!entry.source || entry.source === '_general') continue
              if (entry.type === 'trigger') {
                sourceCount[entry.source] = (sourceCount[entry.source] || 0) + 1
                runGroups.push({
                  source: entry.source,
                  entries: [entry],
                  runJobId: entry.job_run_id,
                  runRegion: entry.aws_region,
                  runIdx: sourceCount[entry.source],
                })
              } else {
                // Match by job_run_id when available, otherwise fall back to most recent group
                let target: RunGroup | undefined
                if (entry.job_run_id) {
                  target = runGroups.find(g => g.runJobId === entry.job_run_id)
                }
                if (!target) {
                  for (let i = runGroups.length - 1; i >= 0; i--) {
                    if (runGroups[i].source === entry.source) { target = runGroups[i]; break }
                  }
                }
                if (target) {
                  target.entries.push(entry)
                } else {
                  sourceCount[entry.source] = (sourceCount[entry.source] || 0) + 1
                  runGroups.push({ source: entry.source, entries: [entry], runIdx: sourceCount[entry.source] })
                }
              }
            }
            // Newest run first
            const displayGroups = [...runGroups].reverse()
            return (
              <div className="activity-log">
                <div className="activity-log-header">
                  <span className="activity-log-title">TRIGGERED RUNS</span>
                </div>
                <div className="activity-log-runs-wrapper">
                  <div className="activity-log-runs">
                    {displayGroups.map((group, gi) => {
                      const lastEntry = group.entries[group.entries.length - 1]
                      const isComplete = lastEntry?.type === 'complete'
                      const isError = lastEntry?.type === 'error'
                      const isRunning = !isComplete && !isError && lastEntry?.type !== 'trigger'
                      const laneStatus = isError ? 'error' : isComplete ? 'complete' : isRunning ? 'running' : 'pending'
                      const matchedRun = group.runJobId
                        ? activeJobRuns.find(r => r.job_run_id === group.runJobId)
                        : undefined
                      const cwRegion = matchedRun?.aws_region || group.runRegion
                      const cwJobId = matchedRun?.job_run_id || group.runJobId
                      return (
                        <div key={group.runJobId ?? `${group.source}-${group.runIdx}`} className={`run-card run-${laneStatus}`}>
                          <div className="run-card-header">
                            <span className={`run-status-dot run-dot-${laneStatus}`} />
                            <span className="run-name">{group.source}</span>
                            {cwRegion && cwJobId && (
                              <span className="run-links run-links-header">
                                <a href={buildCloudWatchUrl(cwJobId, cwRegion, 'output')} target="_blank" rel="noopener noreferrer" className="run-link" title="Output log">Log</a>
                                <a href={buildCloudWatchUrl(cwJobId, cwRegion, 'error')} target="_blank" rel="noopener noreferrer" className="run-link run-link-error" title="Error log">Err</a>
                              </span>
                            )}
                            <button className="run-card-close" onClick={() => dismissRunGroup(group.entries)} title="Dismiss">&times;</button>
                          </div>
                          {(() => {
                            const logData = group.runJobId ? lambdaLogsByRunId.get(group.runJobId) : undefined
                            const isLogPanelOpen = group.runJobId ? expandedLogPanelByRunId.has(group.runJobId) : false
                            const toggleLogPanel = () => {
                              if (!group.runJobId) return
                              setExpandedLogPanelByRunId(prev => {
                                const next = new Set(prev)
                                next.has(group.runJobId!) ? next.delete(group.runJobId!) : next.add(group.runJobId!)
                                return next
                              })
                            }
                            return (
                              <>
                                <div className="run-card-entries">
                                  {group.entries.map((entry, i) => {
                                    const isLastEntry = i === group.entries.length - 1
                                    const isToggleable = isLastEntry && !!logData
                                    return (
                                      <div
                                        key={i}
                                        className={`activity-log-entry activity-log-${entry.type}${isToggleable ? ' activity-log-entry--toggleable' : ''}`}
                                        onClick={isToggleable ? toggleLogPanel : undefined}
                                      >
                                        <span className="activity-log-time">{entry.time}</span>
                                        <span className="activity-log-message">{entry.message}</span>
                                        {isToggleable && (
                                          <svg className={`activity-log-logs-chevron${isLogPanelOpen ? ' expanded' : ''}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="6 9 12 15 18 9" />
                                          </svg>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                                {logData && isLogPanelOpen && (() => {
                                  const expandedStreams = expandedStreamsByRunId.get(group.runJobId!) || new Set<string>()
                                  const toggleStream = (name: string) => setExpandedStreamsByRunId(prev => {
                                    const next = new Map(prev)
                                    const streams = new Set(next.get(group.runJobId!) || new Set<string>())
                                    streams.has(name) ? streams.delete(name) : streams.add(name)
                                    next.set(group.runJobId!, streams)
                                    return next
                                  })
                                  const formatTs = (ms: number | undefined) =>
                                    ms ? utilService.formatDateTime(new Date(ms).toISOString()) : '—'
                                  const isStreamError = (events: any[]) =>
                                    events.some(e => /\[ERROR\]|exception|Traceback/i.test(e.message))
                                  return (
                                    <div className="run-card-logs">
                                      {logData.loading ? (
                                        <div className="cons-log-empty">Fetching logs...</div>
                                      ) : !logData.logs?.length ? (
                                        <div className="cons-log-empty">
                                          No logs captured.
                                          {logData.logGroup && (
                                            <a
                                              className="cons-log-cw-link"
                                              href={`https://${glueAwsRegion || 'eu-central-1'}.console.aws.amazon.com/cloudwatch/home?region=${glueAwsRegion || 'eu-central-1'}#logsV2:log-groups/log-group/${encodeURIComponent(encodeURIComponent(logData.logGroup))}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              View in CloudWatch ↗
                                            </a>
                                          )}
                                        </div>
                                      ) : logData.logs[0]?._error ? (
                                        <div className="cons-log-error-banner">
                                          <div className="cons-log-error-title">{logData.logs[0].error_code}</div>
                                          <div className="cons-log-error-msg">{logData.logs[0].error_message}</div>
                                          <div className="cons-log-error-group">Log group: <code>{logData.logs[0].log_group}</code></div>
                                        </div>
                                      ) : (
                                        <div className="cons-log-list">
                                          {logData.logs.map((stream: any, si: number) => {
                                            const isExpanded = expandedStreams.has(stream.stream_name) || (logData.logs!.length === 1 && si === 0)
                                            const hasErr = isStreamError(stream.events || [])
                                            return (
                                              <div key={stream.stream_name} className={`cons-log-item${hasErr ? ' cons-log-item--error' : ''}`}>
                                                <button className="cons-log-summary" onClick={() => toggleStream(stream.stream_name)}>
                                                  <span className={`cons-log-dot${hasErr ? ' cons-dot--error' : ' cons-dot--ok'}`} />
                                                  <span className="cons-log-time">{formatTs(stream.last_event_timestamp)}</span>
                                                  <span className="cons-log-file">{stream.stream_name}</span>
                                                  <svg className={`cons-log-chevron${isExpanded ? ' expanded' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="6 9 12 15 18 9" />
                                                  </svg>
                                                </button>
                                                {isExpanded && (
                                                  <div className="cons-log-detail">
                                                    <pre className="cons-detail-pre" ref={el => { if (el) el.scrollTop = el.scrollHeight }}>
                                                      {(stream.events || []).map((e: any) => e.message).join('\n') || '(no events)'}
                                                    </pre>
                                                  </div>
                                                )}
                                              </div>
                                            )
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  )
                                })()}
                              </>
                            )
                          })()}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          })()}
        </div>

        <div className="pipeline-right">
          <div className="dl-wp-section-head">
            <div className="section-header">DOWNLOADS</div>
            <button
              className="dl-wp-history-btn"
              onClick={() => setIsHistoricalWpOpen(true)}
              type="button"
              title="Download historical Working Paper"
              aria-label="Download historical Working Paper"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 7v5l3 2" />
                <path d="M8 3.5A9.5 9.5 0 0 1 12 2" />
              </svg>
            </button>
          </div>
          <div className="dl-wp-row">
            <button
              className={`dl-wp dl-${wpStatus}`}
              onClick={() => generateWorkingPaper()}
              disabled={wpStatus === 'generating' || !hasAnyHandlerHistory}
            >
              <div className="dl-wp-icon">
                {wpStatus === 'generating'
                  ? <span className="dl-spinner" />
                  : wpStatus === 'success'
                    ? <IconCheck />
                    : <IconDownload />
                }
              </div>
              <div className="dl-wp-body">
                <span className="dl-wp-title">
                  {wpStatus === 'generating' ? 'Generating...' : wpStatus === 'success' ? 'Downloaded' : 'Working Paper + IPEs'}
                </span>
                <span className="dl-wp-sub">
                  {workingPaperSourceDateTime ? (
                    <>
                      Source session: 
                      <time dateTime={new Date(workingPaperSourceDateTime).toISOString()}>
                        {workingPaperSourceIteration?.label || 'unknown time'}
                      </time>
                    </>
                  ) : (
                    'Working Paper + IPEs download'
                  )}
                </span>
              </div>
            </button>
          </div>
          {hasAnyHandlerHistory ? (
            shouldShowWorkingPaperWarning({
              hasAnyHandlerHistory,
              workingPaperSourceIterationExists: !!workingPaperSourceIteration,
              isWorkingPaperAligned,
              currentSessionHasControlResultId,
              isWorkingPaperSameDayAsCurrentSession,
            }) ? (
              <div className="partial-warning dl-wp-warning">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>
                  {currentSessionHasControlResultId ? (
                    <>
                      The Working Paper + IPEs download will come from session{' '}
                      {workingPaperSourceDateTime ? (
                        <time dateTime={new Date(workingPaperSourceDateTime).toISOString()}>
                          {workingPaperSourceIteration?.label || 'unknown time'}
                        </time>
                      ) : (
                        'unknown time'
                      )}
                      .
                    </>
                  ) : !isWorkingPaperSameDayAsCurrentSession ? (
                    <>
                      This session does not expose a control result ID.
                      {' '}
                      Use Historical Working Paper Download to choose a past session and download its Working Paper + IPEs.
                    </>
                  ) : null}
                </span>
              </div>
            ) : null
          ) : (
            <div className="partial-warning dl-wp-warning">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                This session does not expose a control result ID.
                {' '}
                Use Historical Working Paper Download to choose a past session and download its Working Paper + IPEs.
              </span>
            </div>
          )}
          

          {data.artifacts.length > 0 && (
            <div className="dl-artifacts-label">Pipeline Files</div>
          )}
          <div className="dl-artifacts">
            {data.artifacts.length === 0 ? (
              <span className="dl-artifacts-empty">No pipeline artifacts in this iteration</span>
            ) : (() => {
              const grouped: Record<string, Artifact[]> = {}
              for (const a of data.artifacts) {
                const stage = a.fileSize || 'other'
                if (!grouped[stage]) grouped[stage] = []
                grouped[stage].push(a)
              }
              return Object.entries(grouped).map(([stage, artifacts]) => (
                <React.Fragment key={stage}>
                  {artifacts.map((artifact, i) => (
                    <button
                      key={`${stage}-${i}`}
                      className={`dl-file${downloadingFile === artifact.fileName ? ' dl-downloading' : ''}`}
                      onClick={() => downloadArtifact(artifact.fileName)}
                      disabled={downloadingFile !== null}
                      title={artifact.fileName.split('/').pop()}
                    >
                      {downloadingFile === artifact.fileName
                        ? <span className="dl-spinner dl-spinner-sm" />
                        : <span className="dl-file-badge">
                            {artifact.type === 'pdf' ? <IconFilePdf /> : artifact.type === 'csv' ? <IconFileCsv /> : <IconFileJson />}
                          </span>
                      }
                      <span className="dl-file-stage">{stage}</span>
                      <span className="dl-file-name">{artifact.fileName.split('/').pop()}</span>
                    </button>
                  ))}
                </React.Fragment>
              ))
            })()}
          </div>

          <div className="ac-external-links-section">
            <div className="section-header">AUDIT CONTROL LINKS</div>
            <div className="ac-external-links">
              <a
                href={`https://${companyName}${envService.dnsSuffix}/control-results?auditControlId=${auditControlId}&isFilterByThread=0`}
                target="_blank"
                rel="noopener noreferrer"
                className="ac-external-link ac-external-link--auditech"
                onClick={(e) => e.stopPropagation()}
              >
                AudiTech
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="7 17 17 7" />
                  <polyline points="7 7 17 7 17 17" />
                </svg>
              </a>
            </div>
          </div>
          <HistoricalWorkingPaperModal
            isOpen={isHistoricalWpOpen}
            auditControlId={auditControlId}
            companyName={companyName}
            initialDate={historicalWpInitialDate}
            onClose={() => setIsHistoricalWpOpen(false)}
            onDownload={generateWorkingPaper}
          />
        </div>
      </div>

      {pendingTrigger && (
        <div className="trigger-confirm-overlay" onClick={cancelTrigger}>
          <div className="trigger-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trigger-confirm-header">
              Confirm Trigger: {pendingTrigger.label}
            </div>
            <div className="trigger-confirm-body">
              {(pendingTrigger.preview.actions || []).map((action: any, i: number) => (
                <div key={i} className="trigger-confirm-action">
                  {action.action === 'glue:StartJobRun' && (
                    <>
                      <div className="trigger-confirm-action-type">Glue Job</div>
                      <div className="trigger-confirm-params">
                        <div className="trigger-confirm-param">
                          <span className="param-key">Job Name</span>
                          <span className="param-value">{action.job_name}</span>
                        </div>
                        {action.arguments && Object.entries(action.arguments).map(([k, v]) => (
                          <div key={k} className="trigger-confirm-param">
                            <span className="param-key">{k.replace(/^--/, '')}</span>
                            <span className="param-value">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {action.action === 'lambda:InvokeFunction' && (
                    <>
                      <div className="trigger-confirm-action-type">Lambda Invocation</div>
                      <div className="trigger-confirm-params">
                        <div className="trigger-confirm-param">
                          <span className="param-key">Function</span>
                          <span className="param-value">{action.function_name}</span>
                        </div>
                        {action.payload && Object.entries(action.payload).map(([k, v]) => (
                          <div key={k} className="trigger-confirm-param">
                            <span className="param-key">{k}</span>
                            <span className="param-value">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {pendingTrigger.hasGenericJobs && (
                <div className="trigger-generic-option">
                  <div className="generic-option-title">Run Scope</div>
                  <label className="generic-option-label">
                    <input
                      type="radio"
                      name="run-scope"
                      checked={genericAcIdOverride === ''}
                      onChange={() => setGenericAcIdOverride('')}
                    />
                    Run all audit controls for this entity + system
                  </label>
                  <label className="generic-option-label">
                    <input
                      type="radio"
                      name="run-scope"
                      checked={genericAcIdOverride !== ''}
                      onChange={() => setGenericAcIdOverride(String(auditControlId))}
                    />
                    Run for specific Audit Control ID(s) only
                  </label>
                  {genericAcIdOverride !== '' && (
                    <input
                      type="text"
                      className="generic-ac-id-input"
                      value={genericAcIdOverride}
                      onChange={(e) => setGenericAcIdOverride(e.target.value)}
                      placeholder="e.g. 7062, 7063, 7064"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              )}
              {pendingTrigger.payload.stage === 'raw' && (
                <div className="trigger-generic-option">
                  <div className="generic-option-title">ETL Options</div>
                  {auditControl?.isAutomated === false && (
                    <label className="generic-option-label generic-option-label--warning">
                      <input
                        type="checkbox"
                        checked={activateAcFirst}
                        onChange={(e) => setActivateAcFirst(e.target.checked)}
                      />
                      This audit control is not active. Activate it before running?
                    </label>
                  )}
                  <label className="generic-option-label">
                    <input
                      type="checkbox"
                      checked={isOutdatedFileAllowed}
                      onChange={(e) => setIsOutdatedFileAllowed(e.target.checked)}
                    />
                    Allow outdated files (is_outdated_data_allowed)
                  </label>
                </div>
              )}
              <p className="trigger-confirm-message">{pendingTrigger.preview.message}</p>
            </div>
            <div className="trigger-confirm-footer">
              <button className="trigger-confirm-cancel" onClick={cancelTrigger} disabled={triggeringStage !== null}>Cancel</button>
              <button className="trigger-confirm-execute" onClick={confirmTrigger} disabled={triggeringStage !== null}>
                {triggeringStage !== null ? <><span className="dl-spinner dl-spinner-sm" />Triggering...</> : 'Trigger'}
              </button>
            </div>
          </div>
        </div>
      )}

      {consolidationWarning && createPortal(
        <div className="execution-pipeline">
          <div className="trigger-confirm-overlay" onClick={() => setConsolidationWarning(null)}>
            <div className="trigger-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="trigger-confirm-header">
                Manual Consolidation Required
              </div>
              <div className="trigger-confirm-body">
                <p className="trigger-confirm-message">
                  {consolidationWarning.triggeredLabel} was triggered, but consolidation cannot run automatically yet because the following required daily system{consolidationWarning.staleSystems.length !== 1 ? 's have' : ' has'} not been normalized in the last 12 hours:
                </p>
                <ul style={{ margin: '8px 0 12px 20px' }}>
                  {consolidationWarning.staleSystems.map(name => (<li key={name}>{name}</li>))}
                </ul>
                <p className="trigger-confirm-message">
                  Trigger consolidation manually if you want this raw run to update the rest of the flow.
                </p>
              </div>
              <div className="trigger-confirm-footer">
                <button className="trigger-confirm-execute" onClick={() => setConsolidationWarning(null)}>Got it</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ExecutionPipeline
